import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import {
  DEFAULT_THEME_ID,
  THEME_ACCENT_OVERRIDES_STORAGE_KEY,
  THEME_BACKGROUND_OVERRIDES_STORAGE_KEY,
  THEME_FAMILY_STORAGE_KEY,
  THEME_ID_STORAGE_KEY,
  THEME_TEXT_OVERRIDES_STORAGE_KEY,
  applyThemeToDocument,
  resolveThemeId,
  sanitizeThemeAccentOverrides,
  sanitizeThemeBackgroundOverrides,
  sanitizeThemeTextOverrides,
} from './utils/themes';
import {
  CALENDAR_CARD_APPEARANCE_STORAGE_KEY,
  sanitizeCalendarCardAppearance,
} from './utils/calendarCardAppearance';

// Ensure the last selected theme is set before React renders
(function() {
  try {
    const resolvedThemeId = resolveThemeId(
      localStorage.getItem(THEME_ID_STORAGE_KEY),
      localStorage.getItem(THEME_FAMILY_STORAGE_KEY)
    );
    const accentOverrides = sanitizeThemeAccentOverrides(
      JSON.parse(localStorage.getItem(THEME_ACCENT_OVERRIDES_STORAGE_KEY) || '{}')
    );
    const backgroundOverrides = sanitizeThemeBackgroundOverrides(
      JSON.parse(localStorage.getItem(THEME_BACKGROUND_OVERRIDES_STORAGE_KEY) || '{}')
    );
    const textOverrides = sanitizeThemeTextOverrides(
      JSON.parse(localStorage.getItem(THEME_TEXT_OVERRIDES_STORAGE_KEY) || '{}')
    );
    const calendarCardAppearance = sanitizeCalendarCardAppearance(
      JSON.parse(localStorage.getItem(CALENDAR_CARD_APPEARANCE_STORAGE_KEY) || 'null')
    );
    applyThemeToDocument(
      resolvedThemeId,
      accentOverrides,
      backgroundOverrides,
      textOverrides,
      calendarCardAppearance
    );
  } catch (e) {
    applyThemeToDocument(DEFAULT_THEME_ID);
  }
})();

// Dev-only: suppress generic cross-origin "Script error." events from 3rd-party widgets
// before CRA's error overlay sees them. Keep production behavior unchanged.
if (process.env.NODE_ENV === 'development') {
  const swallowTradingViewScriptError = (event) => {
    // `ErrorEvent` shape: https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent
    // Cross-origin script errors often show up as:
    // - message: "Script error."
    // - error: null/undefined (no stack)
    // CRA overlay may report the source as bundle.js even though the origin is 3rd-party.
    const msg = event?.message;
    const filename = event?.filename;
    const hasNoRealErrorObject = !event?.error;
    const looksLikeGenericScriptError =
      msg === 'Script error.' &&
      hasNoRealErrorObject &&
      (!filename || String(filename).includes('/static/js/'));

    if (looksLikeGenericScriptError) {
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      return;
    }
  };

  const swallowTradingViewUnhandledRejection = (event) => {
    // Some widget failures can surface as unhandled rejections.
    const reason = event?.reason;
    const reasonMsg =
      (typeof reason === 'string' && reason) ||
      (reason && typeof reason === 'object' && reason.message) ||
      '';

    const looksLikeGenericScriptError =
      reasonMsg === 'Script error.' || reasonMsg === 'Script error';

    if (looksLikeGenericScriptError) {
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      return;
    }
  };

  window.addEventListener('error', swallowTradingViewScriptError, true);
  window.addEventListener('unhandledrejection', swallowTradingViewUnhandledRejection, true);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
